import { useState } from "react";
import { MK_CSS, MarketingNav, MarketingFooter, useMkNav } from "./MarketingChrome";

const CSS = `
.pp-toggle { display:flex; flex-direction:column; align-items:center; gap:12px; margin-bottom:48px; }
.pp-seg { position:relative; display:inline-flex; align-items:center; padding:4px; border-radius:100px; background:var(--panel); box-shadow:inset 0 0 0 1px rgba(2,2,2,.08); }
.pp-knob { position:absolute; top:4px; left:4px; width:calc(50% - 4px); height:calc(100% - 8px); border-radius:100px; background:#fff; box-shadow:0 1px 2px rgba(2,2,2,.12); transition:transform .25s ease; }
.pp-knob.yr { transform:translateX(100%); }
.pp-seg button { position:relative; z-index:1; border:none; background:none; cursor:pointer; padding:8px 24px; font-family:inherit; font-size:15px; font-weight:500; color:var(--muted); transition:color .2s; }
.pp-seg button.on { color:var(--ink); }
.pp-save { font-size:14px; color:var(--olive); font-weight:500; }

.pp-cards { display:grid; grid-template-columns:repeat(3,1fr); align-items:start; gap:32px; }
.pp-card { position:relative; display:flex; flex-direction:column; background:#fff; color:var(--ink); border-radius:8px; padding:40px 32px; box-shadow:inset 0 0 0 1px rgba(2,2,2,.1); }
.pp-card.dark { background:var(--dark); color:#fff; box-shadow:inset 0 0 0 2px var(--olive); }
.pp-card h2 { margin-bottom:8px; font-size:22px; }
.pp-card .desc { font-size:16px; color:var(--muted); }
.pp-card.dark .desc { color:rgba(255,255,255,.7); }
.pp-div { height:1px; width:100%; background:rgba(2,2,2,.12); margin:32px 0; }
.pp-card.dark .pp-div { background:rgba(255,255,255,.2); }
.pp-price { display:flex; align-items:baseline; gap:6px; }
.pp-price h3 { font-size:64px; line-height:1; }
.pp-price span { font-size:18px; color:var(--muted); }
.pp-card.dark .pp-price span { color:rgba(255,255,255,.7); }
.pp-sub { margin-top:8px; font-size:16px; color:var(--muted); }
.pp-card.dark .pp-sub { color:rgba(255,255,255,.7); }
.pp-badge { position:absolute; top:24px; right:24px; display:inline-flex; align-items:center; border-radius:100px; padding:4px 14px; font-size:13px; font-weight:600; color:var(--dark); background:var(--lolive); }
.pp-feats-head { margin-bottom:20px; font-size:14px; font-weight:600; color:var(--muted); }
.pp-card.dark .pp-feats-head { color:rgba(255,255,255,.7); }
.pp-feats { display:flex; flex-direction:column; gap:16px; }
.pp-feat { display:flex; align-items:flex-start; gap:16px; font-size:16px; line-height:1.5; }
.pp-feat .ck { flex:0 0 auto; margin-top:2px; display:inline-flex; }

.pp-faq { display:grid; grid-template-columns:1fr 1fr; gap:24px; }
.pp-faq-item { background:var(--soft); border-radius:8px; padding:24px 28px; }
.pp-faq-item h3 { font-family:"Inter",sans-serif; font-weight:600; font-size:18px; margin-bottom:10px; }
.pp-faq-item p { font-size:16px; line-height:1.5; color:var(--muted); }

@media (max-width:900px){ .pp-cards { grid-template-columns:1fr; } .pp-faq { grid-template-columns:1fr; } }
`;

const Check = ({ color }: { color: string }) => (
  <span className="ck"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 13l4 4L19 7" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg></span>
);

const PLANS = [
  {
    name: "Starter", dark: false, desc: "For a single independent venue.",
    mo: "$19", yr: "$15.83", subMo: "Billed monthly", subYr: "$190 billed yearly · save 2 months",
    cta: "Start trial", ctaClass: "mk-btn-soft", check: "#607840",
    head: "Everything you need to get clean",
    feats: ["Unlimited invoice capture", "AI line-item extraction", "Full data export", "Spend reporting"],
  },
  {
    name: "Growth", dark: true, popular: true, desc: "For small groups with multiple locations.",
    mo: "$29", yr: "$24.17", subMo: "Billed monthly", subYr: "$290 billed yearly · save 2 months",
    cta: "Start trial", ctaClass: "mk-btn-primary", check: "#a9bf87",
    head: "Everything in Starter, plus",
    feats: ["Everything in Starter", "Multi-location support", "Menu costing tools", "Price movement tracking", "Supplier catalog management"],
  },
  {
    name: "Enterprise", dark: false, desc: "For larger F&B groups and custom needs.",
    mo: "$49", yr: "$40.83", subMo: "Billed monthly", subYr: "$490 billed yearly · save 2 months",
    cta: "Talk to sales", ctaClass: "mk-btn-soft", check: "#607840",
    head: "Everything in Growth, plus",
    feats: ["Everything in Growth", "Custom integrations", "Dedicated support", "Advanced permissions", "Par-level checks", "Audit-ready reporting"],
  },
];

const FAQS = [
  { q: "Can I switch plans anytime?", a: "Yes. Change your plan anytime from the dashboard. The new rate applies to your next billing cycle — no penalties, no calls required." },
  { q: "Is there a setup fee?", a: "No. There are no setup fees or hidden costs. You start capturing invoices the same day you sign up." },
  { q: "What counts as a location?", a: "A location is a single physical venue with its own supplier relationships. A café and a restaurant under the same group are two locations." },
  { q: "Do you offer annual discounts?", a: "Yes. Annual billing saves you roughly two months compared to paying monthly. The exact saving is shown on each plan card." },
  { q: "Can I export my data if I cancel?", a: "Absolutely. Export your invoices, line items, and reports as CSV or PDF at any time. Your data belongs to you, even if you cancel." },
  { q: "How long does onboarding take?", a: "Most venues are capturing invoices within a day. There’s no training manual — snap an invoice and the data files itself." },
];

export default function PricingPage() {
  const nav = useMkNav();
  const [period, setPeriod] = useState<"mo" | "yr">("mo");

  return (
    <div className="mk">
      <style>{MK_CSS}</style>
      <style>{CSS}</style>

      <MarketingNav active="pricing" />

      {/* HEADER */}
      <section className="mk-sec dark">
        <div className="mk-narrow">
          <p className="mk-eyebrow head" style={{ color: "var(--lolive)" }}>Pricing</p>
          <h1>Simple, transparent pricing</h1>
          <p>Every plan includes unlimited invoices, AI extraction, and full export capabilities. Pay only for the scale you need.</p>
          <div className="mk-cta-row">
            <button className="mk-btn mk-btn-lg mk-btn-primary" onClick={nav.goSignup}>Get started</button>
            <button className="mk-btn mk-btn-lg mk-btn-glass" onClick={nav.goAbout}>Talk to us</button>
          </div>
        </div>
      </section>

      {/* PLANS */}
      <section className="mk-sec">
        <div className="mk-wrap">
          <div className="mk-center-head" style={{ maxWidth: 512, marginBottom: 48 }}>
            <p className="mk-eyebrow head" style={{ color: "var(--olive)" }}>Plans</p>
            <h2>Choose your scale</h2>
            <p>Whether it's one venue or a growing group, there's a plan that fits.</p>
          </div>

          <div className="pp-toggle">
            <div className="pp-seg">
              <span className={`pp-knob${period === "yr" ? " yr" : ""}`} />
              <button className={period === "mo" ? "on" : ""} onClick={() => setPeriod("mo")}>Monthly</button>
              <button className={period === "yr" ? "on" : ""} onClick={() => setPeriod("yr")}>Annual</button>
            </div>
            <p className="pp-save">Save 2 months with annual billing</p>
          </div>

          <div className="pp-cards">
            {PLANS.map((p) => (
              <div className={`pp-card${p.dark ? " dark" : ""}`} key={p.name}>
                {p.popular && <div className="pp-badge">Most popular</div>}
                <h2>{p.name}</h2>
                <p className="desc">{p.desc}</p>
                <div className="pp-div" />
                <div className="pp-price">
                  <h3>{period === "yr" ? p.yr : p.mo}</h3>
                  <span>/mo</span>
                </div>
                <p className="pp-sub">{period === "yr" ? p.subYr : p.subMo}</p>
                <button
                  className={`mk-btn mk-btn-lg ${p.ctaClass} wide`}
                  style={{ marginTop: 32 }}
                  onClick={() => (p.cta === "Talk to sales" ? nav.goAbout() : nav.goSignup())}
                >{p.cta}</button>
                <div className="pp-div" />
                <p className="pp-feats-head">{p.head}</p>
                <div className="pp-feats">
                  {p.feats.map((f) => (
                    <div className="pp-feat" key={f}><Check color={p.check} /><p>{f}</p></div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mk-sec" style={{ background: "var(--cream)" }}>
        <div className="mk-wrap">
          <div style={{ maxWidth: 768, marginBottom: 64 }}>
            <h2 style={{ marginBottom: 24, fontSize: "clamp(32px,5vw,52px)" }}>FAQs</h2>
            <p style={{ fontSize: 18, lineHeight: 1.5 }}>Straight answers for operators who need to move fast and keep costs down.</p>
          </div>
          <div className="pp-faq">
            {FAQS.map((f) => (
              <div className="pp-faq-item" key={f.q}>
                <h3>{f.q}</h3>
                <p>{f.a}</p>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 64 }}>
            <h3 style={{ marginBottom: 12, fontSize: 36 }}>Still have questions?</h3>
            <p style={{ fontSize: 18, lineHeight: 1.5 }}>Our team knows kitchens and bars, not just software.</p>
            <div style={{ marginTop: 24 }}>
              <button className="mk-btn mk-btn-lg mk-btn-soft" onClick={nav.goAbout}>Contact us</button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mk-sec dark">
        <div className="mk-narrow">
          <h2>Find the right plan together</h2>
          <p>We can walk you through the numbers for your specific operation — no pressure and no pitch decks.</p>
          <div className="mk-cta-row">
            <button className="mk-btn mk-btn-lg mk-btn-primary" onClick={nav.goAbout}>Book a demo</button>
            <button className="mk-btn mk-btn-lg mk-btn-glass" onClick={nav.goAbout}>Talk to sales</button>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
